import { useState } from "react";
import { useApolloClient, useReactiveVar } from "@apollo/client";
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { userEntityRelationVar } from "../../../client/cache";
import auditIncomingOrderOpenQuantities from "../../../function/utilityAPI/auditIncomingOrderOpenQuantities";
import { toChinaDateString } from "../../../lib/chinaDate";


function exportCSV(rows) {
  const headers = [
    "PO Number",
    "Item Number",
    "Vendor",
    "Material Code",
    "Status Code",
    "CRD",
    "Ordered Qty",
    "Current Open Qty",
    "Total Shipped Qty",
    "Correct Open Qty",
    "Discrepancy",
    "Has Discrepancy",
    "Error",
  ];

  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const csvRows = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.number,
        r.itemNumber,
        r.vendorName,
        r.materialCode,
        r.statusCode,
        r.CRD,
        r.orderedQty,
        r.currentOpenQty,
        r.totalShippedQty,
        r.correctOpenQty,
        r.discrepancy,
        r.hasDiscrepancy,
        r.error || "",
      ]
        .map(escape)
        .join(",")
    ),
  ];

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `open_qty_audit_${toChinaDateString()}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function FixOpenQuantities() {
  const client = useApolloClient();
  const { entityID } = useReactiveVar(userEntityRelationVar);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showAllRows, setShowAllRows] = useState(false);

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    setProgress({ current: 0, total: 0 });

    try {
      const data = await auditIncomingOrderOpenQuantities({
        client,
        definerID: entityID,
        onProgress: (current, total) => setProgress({ current, total }),
      });
      setResults(data);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const displayRows = results
    ? showAllRows
      ? results
      : results.filter((r) => r.hasDiscrepancy)
    : [];

  const discrepancyCount = results ? results.filter((r) => r.hasDiscrepancy).length : 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack spacing={3}>
        <Typography variant="h4">Audit Incoming Order Open Quantities</Typography>
        <Typography variant="body2" color="text.secondary">
          Fetches all POs with open quantity &gt; 0, sums their shipment records, and
          compares the result against the stored open quantity. Read-only — no data is
          modified.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            onClick={handleRun}
            disabled={loading || !entityID}
          >
            {loading ? <CircularProgress size={20} /> : "Run Audit"}
          </Button>

          {results && (
            <Button
              variant="outlined"
              onClick={() => exportCSV(showAllRows ? results : displayRows)}
            >
              Export CSV
            </Button>
          )}
        </Stack>

        {loading && progress.total > 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Processing PO {progress.current} of {progress.total}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
            />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {results && (
          <Stack spacing={2}>
            <Alert severity={discrepancyCount > 0 ? "warning" : "success"}>
              Total POs scanned: {results.length} | Discrepancies found:{" "}
              {discrepancyCount} | Errors: {results.filter((r) => r.error).length}
            </Alert>

            <FormControlLabel
              control={
                <Switch
                  checked={showAllRows}
                  onChange={(e) => setShowAllRows(e.target.checked)}
                />
              }
              label="Show all POs (including correct ones)"
            />

            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Item #</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Material Code</TableCell>
                      <TableCell>CRD</TableCell>
                      <TableCell align="right">Ordered Qty</TableCell>
                      <TableCell align="right">Current Open Qty</TableCell>
                      <TableCell align="right">Total Shipped</TableCell>
                      <TableCell align="right">Correct Open Qty</TableCell>
                      <TableCell align="right">Discrepancy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          {showAllRows ? "No POs found" : "No discrepancies found"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayRows.map((row, idx) => (
                        <TableRow
                          key={idx}
                          sx={
                            row.error
                              ? { backgroundColor: "error.light" }
                              : row.hasDiscrepancy
                              ? { backgroundColor: "warning.light" }
                              : {}
                          }
                        >
                          <TableCell>{row.number}</TableCell>
                          <TableCell>{row.itemNumber}</TableCell>
                          <TableCell>{row.vendorName}</TableCell>
                          <TableCell>{row.materialCode}</TableCell>
                          <TableCell>{row.CRD}</TableCell>
                          <TableCell align="right">{row.orderedQty}</TableCell>
                          <TableCell align="right">{row.currentOpenQty}</TableCell>
                          <TableCell align="right">
                            {row.totalShippedQty ?? "—"}
                          </TableCell>
                          <TableCell align="right">
                            {row.correctOpenQty !== null
                              ? row.correctOpenQty
                              : row.error ?? "—"}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={
                              row.hasDiscrepancy ? { fontWeight: "bold" } : {}
                            }
                          >
                            {row.discrepancy !== null ? row.discrepancy : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
